import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Calendar, Filter, Menu, X } from 'lucide-react';
import AuthScreen from './shared/AuthScreen';
import KPISection from './schoorsteenveger/KPISection';
import RevenueChart from './schoorsteenveger/RevenueChart';
import TeamLeaderboard from './schoorsteenveger/TeamLeaderboard';
import ServicesBreakdown from './schoorsteenveger/ServicesBreakdown';
import OutstandingInvoices from './schoorsteenveger/OutstandingInvoices';
import { getAllDashboardData } from '../utils/schoorsteenvegerQueries';
import { apiPost, setToken, hasValidToken } from '../utils/apiClient';
import { dashboardCache } from '../utils/performanceCache';
import { useAudio } from '../hooks/useAudio';

// Period selector component with design system styling
const PeriodSelector = ({ selectedPeriod, onPeriodChange, onCustomDateChange, customStartDate, customEndDate, isMobileMenuOpen, setIsMobileMenuOpen }) => {

  const periods = [
    { value: 'today', label: 'Vandaag' },
    { value: 'yesterday', label: 'Gisteren' },
    { value: 'thisWeek', label: 'Deze week' },
    { value: 'lastWeek', label: 'Vorige week' },
    { value: 'thisMonth', label: 'Deze maand' },
    { value: 'lastMonth', label: 'Vorige maand' },
    { value: 'thisYear', label: 'Dit jaar' }
  ];

  return (
    <>
      {/* Mobile dropdown menu */}
      {isMobileMenuOpen && (
        <div className="mobile-menu-blue">
          <div className="menu-section">
            <div className="menu-label">
              <Filter className="w-4 h-4" />
              <span>Periode selecteren:</span>
            </div>
            <div className="period-grid">
              {periods.map((period) => (
                <button
                  key={period.value}
                  onClick={() => {
                    onPeriodChange(period.value);
                    setIsMobileMenuOpen(false);
                  }}
                  className={`period-btn ${selectedPeriod === period.value ? 'active' : ''}`}
                  style={{
                    padding: 'var(--space-2) var(--space-3)',
                    fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--font-medium)',
                    borderRadius: 'var(--radius-md)',
                    border: 'none',
                    cursor: 'pointer',
                    background: selectedPeriod === period.value ? 'var(--color-blue-500)' : 'var(--color-neutral-100)',
                    color: selectedPeriod === period.value ? 'white' : 'var(--color-neutral-700)'
                  }}
                >
                  {period.label}
                </button>
              ))}
            </div>
          </div>
          <div className="menu-section" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <Calendar className="w-4 h-4" style={{ color: 'var(--color-neutral-500)' }} />
            <input
              type="date"
              value={customStartDate}
              onChange={(e) => onCustomDateChange('start', e.target.value)}
              className="date-input"
              style={{
                padding: 'var(--space-2)',
                fontSize: 'var(--text-sm)',
                border: '1px solid var(--color-neutral-300)',
                borderRadius: 'var(--radius-md)',
                flex: 1
              }}
              placeholder="Van"
            />
            <span style={{ color: 'var(--color-neutral-500)', fontSize: 'var(--text-sm)' }}>tot</span>
            <input
              type="date"
              value={customEndDate}
              onChange={(e) => onCustomDateChange('end', e.target.value)}
              className="date-input"
              style={{
                padding: 'var(--space-2)',
                fontSize: 'var(--text-sm)',
                border: '1px solid var(--color-neutral-300)',
                borderRadius: 'var(--radius-md)',
                flex: 1
              }}
              placeholder="Tot"
            />
          </div>
        </div>
      )}

      {/* Desktop view */}
      <div className="period-selector-blue filter-bar-desktop">
        <div className="period-label">
          <Filter className="w-4 h-4" />
          <span>Periode:</span>
        </div>
        <div className="period-buttons">
          {periods.map((period) => (
            <button
              key={period.value}
              onClick={() => onPeriodChange(period.value)}
              className={`period-btn ${selectedPeriod === period.value ? 'active' : ''}`}
            >
              {period.label}
            </button>
          ))}
        </div>
        <div className="date-inputs">
          <Calendar className="w-4 h-4" style={{ color: 'var(--color-neutral-500)' }} />
          <input
            type="date"
            value={customStartDate}
            onChange={(e) => onCustomDateChange('start', e.target.value)}
            className="date-input"
            placeholder="Van"
          />
          <span style={{ color: 'var(--color-neutral-500)', fontSize: 'var(--text-sm)' }}>tot</span>
          <input
            type="date"
            value={customEndDate}
            onChange={(e) => onCustomDateChange('end', e.target.value)}
            className="date-input"
            placeholder="Tot"
          />
        </div>
      </div>
    </>
  );
};

const SchoorsteenvegerDashboard = () => {
  // Audio hook
  const { playAudio, AudioElement } = useAudio('/betalen.mp3');

  // Authentication state
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authError, setAuthError] = useState('');

  // Data state
  const [dashboardData, setDashboardData] = useState({
    currentMonthRevenue: 0,
    currentMonthInvoiceCount: 0,
    totalOutstanding: 0,
    averageInvoiceValue: 0,
    employeeData: [],
    serviceData: [],
    outstandingInvoices: [],
    monthlyTrend: []
  });
  const [loading, setLoading] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);

  // Filter state
  const [filters, setFilters] = useState({
    period: 'today',
    startDate: null,
    endDate: null,
    customPeriod: false
  });

  // Mobile menu state
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Authentication handlers — login via backend levert een JWT op
  const handlePasswordSubmit = useCallback(async () => {
    try {
      const res = await apiPost('/login', { type: 'schoorsteenveger', password });
      setToken(res.token);
      setIsAuthenticated(true);
      setAuthError('');
    } catch (error) {
      setAuthError('Onjuist wachtwoord');
    }
  }, [password]);

  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter') {
      handlePasswordSubmit();
    }
  }, [handlePasswordSubmit]);

  // Check authentication on mount — geldige (niet-verlopen) JWT?
  useEffect(() => {
    setIsAuthenticated(hasValidToken());
  }, []);

  // Play audio when authentication is successful
  useEffect(() => {
    if (isAuthenticated) {
      setTimeout(() => {
        playAudio();
      }, 100);
    }
  }, [isAuthenticated, playAudio]);

  // Period change handler
  const handlePeriodChange = useCallback((period) => {
    setFilters(prev => ({
      ...prev,
      period,
      customPeriod: false,
      startDate: null,
      endDate: null
    }));
  }, []);

  // Custom date change handler
  const handleCustomDateChange = useCallback((type, value) => {
    setFilters(prev => ({
      ...prev,
      [type === 'start' ? 'startDate' : 'endDate']: value,
      customPeriod: true,
      period: 'custom'
    }));
  }, []);

  // Get period label for KPI subtitles
  const getPeriodLabel = useCallback((period) => {
    const labels = {
      'today': 'Vandaag',
      'yesterday': 'Gisteren',
      'thisWeek': 'Deze week',
      'lastWeek': 'Vorige week',
      'thisMonth': 'Deze maand',
      'lastMonth': 'Vorige maand',
      'thisYear': 'Dit jaar',
      'custom': 'Aangepaste periode'
    };
    return labels[period] || 'Deze maand';
  }, []);

  // Data loading function
  const loadData = useCallback(async () => {
    if (!filters.customPeriod) {
      const cachedData = dashboardCache.get({ period: filters.period, startDate: filters.startDate, endDate: filters.endDate });
      if (cachedData) {
        setDashboardData(cachedData);
        setLastRefresh(new Date());
        playAudio();
        return;
      }
    }

    setLoading(true);

    try {
      const data = await getAllDashboardData(filters);

      setDashboardData(data);
      setLastRefresh(new Date());
      playAudio();

      if (!filters.customPeriod) {
        dashboardCache.set({ period: filters.period, startDate: filters.startDate, endDate: filters.endDate }, data);
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [filters, playAudio]);

  // Load data when filters change
  useEffect(() => {
    if (isAuthenticated) {
      loadData();
    }
  }, [isAuthenticated, filters, loadData]);

  // Manual refresh handler
  const handleRefresh = useCallback(() => {
    dashboardCache.clear();
    loadData();
  }, [loadData]);

  if (!isAuthenticated) {
    return (
      <AuthScreen
        auth={{
          password,
          setPassword,
          authError,
          handlePasswordSubmit,
          handleKeyPress,
          authType: 'Schoorsteenveger Dashboard',
          loading: false
        }}
        theme="blue"
      />
    );
  }

  return (
    <div className="theme-blue-container" style={{ minHeight: '100vh', background: 'var(--color-neutral-50)' }}>
      {/* Sticky Header */}
      <header className="dashboard-header-blue">
        <div className="header-content">
          <div className="header-brand">
            <div className="header-logo">
              <img
                src="https://mrschoorsteenveger.nl/wp-content/uploads/Mr.-Schoorsteenveger.png"
                alt="Mr. Schoorsteenveger Logo"
              />
            </div>
            <div>
              <h1 className="header-title">Mr. Schoorsteenveger</h1>
              {lastRefresh && (
                <span className="header-subtitle">
                  {lastRefresh.toLocaleDateString('nl-NL')} {lastRefresh.toLocaleTimeString('nl-NL')}
                </span>
              )}
            </div>
          </div>
          <div className="header-actions">
            {/* Mobile menu toggle - only visible on mobile */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="btn-header lg:hidden"
              title="Menu"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="btn-header"
              title="Vernieuwen"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main style={{ maxWidth: '80rem', margin: '0 auto', padding: 'var(--space-6) var(--space-4)' }}>
        {/* Period Selector */}
        <PeriodSelector
          selectedPeriod={filters.period}
          onPeriodChange={handlePeriodChange}
          onCustomDateChange={handleCustomDateChange}
          customStartDate={filters.startDate || ''}
          customEndDate={filters.endDate || ''}
          isMobileMenuOpen={isMobileMenuOpen}
          setIsMobileMenuOpen={setIsMobileMenuOpen}
        />

        {/* KPI Section */}
        <KPISection
          currentMonthRevenue={dashboardData.currentMonthRevenue}
          currentMonthInvoiceCount={dashboardData.currentMonthInvoiceCount}
          totalOutstanding={dashboardData.totalOutstanding}
          averageInvoiceValue={dashboardData.averageInvoiceValue}
          loading={loading}
          periodLabel={getPeriodLabel(filters.period)}
        />

        {/* Charts Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(1, 1fr)',
          gap: 'var(--space-6)',
          marginBottom: 'var(--space-6)'
        }} className="lg:grid-cols-2">
          <TeamLeaderboard data={dashboardData.employeeData} loading={loading} period={filters.period} />
          <ServicesBreakdown data={dashboardData.serviceData} loading={loading} />
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(1, 1fr)',
          gap: 'var(--space-6)'
        }} className="lg:grid-cols-2">
          <RevenueChart data={dashboardData.monthlyTrend} loading={loading} period={filters.period} />
          <OutstandingInvoices data={dashboardData.outstandingInvoices} loading={loading} />
        </div>
      </main>

      {/* Audio element */}
      <AudioElement />

      {/* Responsive grid styles */}
      <style>{`
        @media (min-width: 1024px) {
          .lg\\:grid-cols-2 {
            grid-template-columns: repeat(2, 1fr) !important;
          }
          .lg\\:hidden {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default SchoorsteenvegerDashboard;
